import React, { useState } from 'react';

function ChecklistBuilder({ onSave, onCancel, existingChecklist = null }) {
  const [checklist, setChecklist] = useState(existingChecklist || {
    name: '',
    description: '',
    questions: [],
    schedule: {
      frequency: 'adhoc',
      daysOfWeek: [],
      monthlyPattern: 'date',
      dayOfMonth: 1,
      weekOfMonth: 'first',
      dayOfWeekForMonthly: 2,
      monthsOfYear: [],
      timeOfDay: '09:00',
      priority: 'medium',
      startDate: new Date().toISOString().split('T')[0]
    },
    status: 'draft'
  });

  const [currentQuestion, setCurrentQuestion] = useState({
    type: 'checkbox',
    label: '',
    required: true,
    placeholder: '',
    min: null,
    max: null
  });

  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const handleAddQuestion = () => {
    if (!currentQuestion.label.trim()) {
      alert('Question label is required');
      return;
    }

    const newQuestion = {
      ...currentQuestion,
      id: `q-${Date.now()}`,
      order: editingIndex !== null ? checklist.questions[editingIndex].order : checklist.questions.length + 1
    };

    if (editingIndex !== null) {
      const updated = [...checklist.questions];
      updated[editingIndex] = newQuestion;
      setChecklist({ ...checklist, questions: updated });
      setEditingIndex(null);
    } else {
      setChecklist({ ...checklist, questions: [...checklist.questions, newQuestion] });
    }

    setCurrentQuestion({ type: 'checkbox', label: '', required: true, placeholder: '', min: null, max: null });
    setShowQuestionModal(false);
  };

  const handleDeleteQuestion = (index) => {
    const updated = checklist.questions.filter((_, i) => i !== index);
    setChecklist({ ...checklist, questions: updated });
  };

  const handleEditQuestion = (index) => {
    setCurrentQuestion(checklist.questions[index]);
    setEditingIndex(index);
    setShowQuestionModal(true);
  };

  const handleSave = (status) => {
    if (!checklist.name.trim()) {
      alert('Checklist name is required');
      return;
    }
    if (checklist.questions.length === 0) {
      alert('Add at least one question');
      return;
    }

    if (checklist.schedule.frequency !== 'adhoc') {
      if (checklist.schedule.frequency === 'weekly' && checklist.schedule.daysOfWeek.length === 0) {
        alert('Select at least one day for weekly schedule');
        return;
      }
      if (['quarterly', 'semiannual', 'annual'].includes(checklist.schedule.frequency) &&
          checklist.schedule.monthsOfYear.length === 0) {
        alert('Select at least one month');
        return;
      }
    }

    onSave({
      ...checklist,
      status,
      id: checklist.id || `cl-${Date.now()}`,
      createdAt: checklist.createdAt || new Date().toISOString()
    });
  };

  const getScheduleSummary = () => {
    const s = checklist.schedule;

    if (s.frequency === 'adhoc') return 'Manual trigger only';
    if (s.frequency === 'daily') return `Every day at ${s.timeOfDay}`;

    if (s.frequency === 'weekly') {
      const days = (s.daysOfWeek || []).map(d => ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][d-1]).join(', ');
      return days ? `Every ${days} at ${s.timeOfDay}` : 'Select days';
    }

    if (s.frequency === 'monthly') {
      if (s.monthlyPattern === 'date') {
        const suffix = s.dayOfMonth === 1 ? 'st' : s.dayOfMonth === 2 ? 'nd' : s.dayOfMonth === 3 ? 'rd' : 'th';
        return `On the ${s.dayOfMonth}${suffix} of each month at ${s.timeOfDay}`;
      } else {
        const week = s.weekOfMonth;
        const day = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'][s.dayOfWeekForMonthly - 1];
        return `On the ${week} ${day} of each month at ${s.timeOfDay}`;
      }
    }

    if (s.frequency === 'quarterly') {
      const months = (s.monthsOfYear || []).map(m => ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1]).join(', ');
      return months ? `Quarterly in ${months} at ${s.timeOfDay}` : 'Select months';
    }

    if (s.frequency === 'semiannual') {
      const months = (s.monthsOfYear || []).map(m => ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1]).join(' and ');
      return months ? `Semi-annually in ${months} at ${s.timeOfDay}` : 'Select 2 months';
    }

    if (s.frequency === 'annual') {
      const month = s.monthsOfYear[0] ? ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][s.monthsOfYear[0]-1] : '';
      return month ? `Annually in ${month} at ${s.timeOfDay}` : 'Select month';
    }

    return '';
  };

  return (
    <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg p-8">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          {checklist.id ? 'Edit Checklist' : 'Create New Checklist'}
        </h2>
        <p className="text-gray-600">Build a checklist that will appear in the Priority Queue on schedule</p>
      </div>

      {/* Basic Info */}
      <div className="mb-8">
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-900 mb-2">Checklist Name *</label>
          <input
            type="text"
            value={checklist.name}
            onChange={(e) => setChecklist({ ...checklist, name: e.target.value })}
            placeholder="e.g., Monthly Safety Equipment Check"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-900 mb-2">Description</label>
          <textarea
            value={checklist.description}
            onChange={(e) => setChecklist({ ...checklist, description: e.target.value })}
            placeholder="Brief description..."
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Questions */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">Questions</h3>
          <button
            onClick={() => setShowQuestionModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            + Add Question
          </button>
        </div>

        {checklist.questions.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-600">No questions yet. Click "Add Question" to start.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {checklist.questions.map((q, i) => (
              <div key={q.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-lg font-bold text-gray-600">#{i + 1}</span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded">
                        {q.type.toUpperCase()}
                      </span>
                      <span className={`px-2 py-1 text-xs font-bold rounded ${
                        q.required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {q.required ? 'REQUIRED' : 'OPTIONAL'}
                      </span>
                    </div>
                    <p className="text-gray-900 font-medium">{q.label}</p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button onClick={() => handleEditQuestion(i)} className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded font-medium text-sm">Edit</button>
                    <button onClick={() => handleDeleteQuestion(i)} className="px-3 py-1 text-red-600 hover:bg-red-50 rounded font-medium text-sm">Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Schedule */}
      <div className="mb-8 border-2 border-blue-200 rounded-lg p-6 bg-blue-50">
        <h3 className="text-xl font-bold text-gray-900 mb-4">⊡ Schedule Configuration</h3>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Frequency *</label>
            <select
              value={checklist.schedule.frequency}
              onChange={(e) => setChecklist({ ...checklist, schedule: { ...checklist.schedule, frequency: e.target.value } })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
            >
              <option value="adhoc">Ad-hoc (Manual Only)</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="semiannual">Semi-Annual</option>
              <option value="annual">Annual</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Priority *</label>
            <select
              value={checklist.schedule.priority}
              onChange={(e) => setChecklist({ ...checklist, schedule: { ...checklist.schedule, priority: e.target.value } })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
            >
              <option value="critical">▲ Critical</option>
              <option value="high">● High</option>
              <option value="medium">○ Medium</option>
              <option value="low">◇ Low</option>
            </select>
          </div>
        </div>

        {checklist.schedule.frequency === 'weekly' && (
          <div className="mb-4 p-4 bg-white rounded-lg">
            <label className="block text-sm font-semibold text-gray-900 mb-2">Select Days *</label>
            <div className="flex gap-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => (
                <button
                  key={day}
                  onClick={() => {
                    const dayNum = i + 1;
                    const days = checklist.schedule.daysOfWeek || [];
                    const updated = days.includes(dayNum) ? days.filter(d => d !== dayNum) : [...days, dayNum].sort();
                    setChecklist({ ...checklist, schedule: { ...checklist.schedule, daysOfWeek: updated } });
                  }}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    (checklist.schedule.daysOfWeek || []).includes(i + 1)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
        )}

        {checklist.schedule.frequency === 'monthly' && (
          <div className="mb-4 p-4 bg-white rounded-lg space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Monthly Pattern *</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="date"
                    checked={checklist.schedule.monthlyPattern === 'date'}
                    onChange={(e) => setChecklist({ ...checklist, schedule: { ...checklist.schedule, monthlyPattern: e.target.value } })}
                    className="w-4 h-4"
                  />
                  <span>Specific Date (e.g., 15th)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="weekday"
                    checked={checklist.schedule.monthlyPattern === 'weekday'}
                    onChange={(e) => setChecklist({ ...checklist, schedule: { ...checklist.schedule, monthlyPattern: e.target.value } })}
                    className="w-4 h-4"
                  />
                  <span>Specific Weekday (e.g., 2nd Tuesday)</span>
                </label>
              </div>
            </div>

            {checklist.schedule.monthlyPattern === 'date' ? (
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Day of Month *</label>
                <select
                  value={checklist.schedule.dayOfMonth}
                  onChange={(e) => setChecklist({ ...checklist, schedule: { ...checklist.schedule, dayOfMonth: parseInt(e.target.value) } })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
                >
                  {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{day}{day===1?'st':day===2?'nd':day===3?'rd':'th'} of the month</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Week of Month *</label>
                  <select
                    value={checklist.schedule.weekOfMonth}
                    onChange={(e) => setChecklist({ ...checklist, schedule: { ...checklist.schedule, weekOfMonth: e.target.value } })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
                  >
                    <option value="first">First</option>
                    <option value="second">Second</option>
                    <option value="third">Third</option>
                    <option value="fourth">Fourth</option>
                    <option value="last">Last</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Day of Week *</label>
                  <select
                    value={checklist.schedule.dayOfWeekForMonthly}
                    onChange={(e) => setChecklist({ ...checklist, schedule: { ...checklist.schedule, dayOfWeekForMonthly: parseInt(e.target.value) } })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
                  >
                    <option value={1}>Monday</option>
                    <option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option>
                    <option value={4}>Thursday</option>
                    <option value={5}>Friday</option>
                    <option value={6}>Saturday</option>
                    <option value={7}>Sunday</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        {['quarterly', 'semiannual', 'annual'].includes(checklist.schedule.frequency) && (
          <div className="mb-4 p-4 bg-white rounded-lg">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Select Month(s) *
              {checklist.schedule.frequency === 'quarterly' && ' (Select 4 months)'}
              {checklist.schedule.frequency === 'semiannual' && ' (Select 2 months)'}
              {checklist.schedule.frequency === 'annual' && ' (Select 1 month)'}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, i) => (
                <button
                  key={month}
                  onClick={() => {
                    const monthNum = i + 1;
                    const months = checklist.schedule.monthsOfYear || [];
                    let updated;
                    if (checklist.schedule.frequency === 'annual') {
                      updated = [monthNum];
                    } else if (checklist.schedule.frequency === 'semiannual') {
                      if (months.includes(monthNum)) {
                        updated = months.filter(m => m !== monthNum);
                      } else if (months.length < 2) {
                        updated = [...months, monthNum].sort();
                      } else {
                        updated = months;
                      }
                    } else {
                      if (months.includes(monthNum)) {
                        updated = months.filter(m => m !== monthNum);
                      } else if (months.length < 4) {
                        updated = [...months, monthNum].sort();
                      } else {
                        updated = months;
                      }
                    }
                    setChecklist({ ...checklist, schedule: { ...checklist.schedule, monthsOfYear: updated } });
                  }}
                  className={`px-3 py-2 rounded-lg font-medium text-sm ${
                    (checklist.schedule.monthsOfYear || []).includes(i + 1)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {month}
                </button>
              ))}
            </div>
          </div>
        )}

        {checklist.schedule.frequency !== 'adhoc' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Time of Day *</label>
              <input
                type="time"
                value={checklist.schedule.timeOfDay}
                onChange={(e) => setChecklist({ ...checklist, schedule: { ...checklist.schedule, timeOfDay: e.target.value } })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Start Date *</label>
              <input
                type="date"
                value={checklist.schedule.startDate}
                onChange={(e) => setChecklist({ ...checklist, schedule: { ...checklist.schedule, startDate: e.target.value } })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
              />
            </div>
          </div>
        )}

        {checklist.schedule.frequency !== 'adhoc' && (
          <div className="mt-4 p-4 bg-blue-100 border-2 border-blue-300 rounded-lg">
            <p className="text-sm font-bold text-gray-900 mb-1">⊡ Schedule Summary:</p>
            <p className="text-sm text-gray-800 font-medium">{getScheduleSummary()}</p>
            <p className="text-xs text-gray-600 mt-2">
              Priority: <span className="font-bold">{checklist.schedule.priority.toUpperCase()}</span> •
              Starts: <span className="font-bold">{checklist.schedule.startDate}</span>
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <button onClick={onCancel} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
          Cancel
        </button>
        <div className="flex gap-3">
          <button onClick={() => handleSave('draft')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
            ◇ Save as Draft
          </button>
          <button onClick={() => handleSave('active')} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium font-bold">
            ✓ Publish Checklist
          </button>
        </div>
      </div>

      {/* Question Modal */}
      {showQuestionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {editingIndex !== null ? 'Edit Question' : 'Add Question'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Question Type *</label>
                <select
                  value={currentQuestion.type}
                  onChange={(e) => setCurrentQuestion({ ...currentQuestion, type: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="checkbox">✓ Checkbox (Yes/No)</option>
                  <option value="text">▢ Text (Free Form)</option>
                  <option value="number">◆ Number (Counts, Temperatures)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Question Label *</label>
                <input
                  type="text"
                  value={currentQuestion.label}
                  onChange={(e) => setCurrentQuestion({ ...currentQuestion, label: e.target.value })}
                  placeholder="e.g., Was the temperature within range?"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              {currentQuestion.type === 'number' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Min Value (Optional)</label>
                    <input
                      type="number"
                      value={currentQuestion.min || ''}
                      onChange={(e) => setCurrentQuestion({ ...currentQuestion, min: e.target.value ? parseFloat(e.target.value) : null })}
                      placeholder="e.g., 36"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Max Value (Optional)</label>
                    <input
                      type="number"
                      value={currentQuestion.max || ''}
                      onChange={(e) => setCurrentQuestion({ ...currentQuestion, max: e.target.value ? parseFloat(e.target.value) : null })}
                      placeholder="e.g., 40"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              )}

              {currentQuestion.type === 'text' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Placeholder (Optional)</label>
                  <input
                    type="text"
                    value={currentQuestion.placeholder || ''}
                    onChange={(e) => setCurrentQuestion({ ...currentQuestion, placeholder: e.target.value })}
                    placeholder="e.g., Enter any concerns..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              )}

              <div className="p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <label htmlFor="required" className="text-base font-bold text-gray-900 cursor-pointer">
                      Is this question required?
                    </label>
                    <p className="text-sm text-gray-600 mt-1">Required questions must be answered before submission</p>
                  </div>
                  <button
                    onClick={() => setCurrentQuestion({ ...currentQuestion, required: !currentQuestion.required })}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                      currentQuestion.required ? 'bg-red-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                      currentQuestion.required ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                <div className="mt-2 text-sm font-bold">
                  {currentQuestion.required ? (
                    <span className="text-red-600">✓ REQUIRED</span>
                  ) : (
                    <span className="text-gray-600">○ OPTIONAL</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => { setShowQuestionModal(false); setEditingIndex(null); }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddQuestion}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                {editingIndex !== null ? 'Update Question' : 'Add Question'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChecklistBuilder;
